/**
 * Tresor — bidirectional document exchange for SB & FL.
 *
 * Two modes:
 *   - mode="freelancer": uploads as own client_id (FL token)
 *   - mode="advisor":    uploads for given client_id (advisor token)
 *
 * Layout: Sidebar with folders (left) + file table (right).
 * Drag&Drop and button upload, search across names, version chips, read-only flag.
 *
 * API:
 *   GET    /api/tresor/folders?client_id=
 *   GET    /api/tresor/documents?client_id=&folder=&search=
 *   POST   /api/tresor/upload   (multipart)
 *   GET    /api/tresor/documents/{id}/download
 *   PATCH  /api/tresor/documents/{id}
 *   DELETE /api/tresor/documents/{id}
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  FolderOpen, Folder, FileText, Lock, Search, Upload, Download, Trash2,
  ShieldCheck, Plus, X, Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };
const MAX_SIZE_MB = 25;

function getHeaders(authMode) {
  const token = localStorage.getItem(authMode === 'advisor' ? 'advisor_token' : 'auth_token');
  return { Authorization: `Bearer ${token}` };
}

function fmtSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Tresor({ mode, clientId }) {
  const authMode = mode === 'advisor' ? 'advisor' : 'freelancer';
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const fetchFolders = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await axios.get(`${API}/tresor/folders`, {
        params: mode === 'advisor' ? { client_id: clientId } : {},
        headers: getHeaders(authMode),
      });
      const list = res.data.folders || [];
      setFolders(list);
      if (!activeFolder && list.length > 0) setActiveFolder(list[0].name);
    } catch {
      // silent
    }
  }, [clientId, mode, authMode, activeFolder]);

  const fetchDocuments = useCallback(async () => {
    if (!clientId || !activeFolder) return;
    setLoading(true);
    try {
      const params = { folder: activeFolder };
      if (mode === 'advisor') params.client_id = clientId;
      if (search) params.search = search;
      const res = await axios.get(`${API}/tresor/documents`, { params, headers: getHeaders(authMode) });
      setDocuments(res.data.documents || []);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, activeFolder, search, mode, authMode]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0 || !activeFolder) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name}: zu groß (max ${MAX_SIZE_MB} MB)`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('folder', activeFolder);
        if (mode === 'advisor') fd.append('client_id', clientId);
        await axios.post(`${API}/tresor/upload`, fd, {
          headers: { ...getHeaders(authMode), 'Content-Type': 'multipart/form-data' },
        });
      }
      toast.success(files.length === 1 ? 'Datei hochgeladen' : `${files.length} Dateien hochgeladen`);
      await Promise.all([fetchFolders(), fetchDocuments()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/tresor/documents/${doc.id}/download`, {
        headers: getHeaders(authMode),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download fehlgeschlagen');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`„${doc.name}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/tresor/documents/${doc.id}`, { headers: getHeaders(authMode) });
      toast.success('Gelöscht');
      await Promise.all([fetchFolders(), fetchDocuments()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const handleToggleReadOnly = async (doc) => {
    try {
      await axios.patch(`${API}/tresor/documents/${doc.id}`,
        { is_read_only: !doc.is_read_only },
        { headers: getHeaders(authMode) });
      await fetchDocuments();
    } catch {
      toast.error('Status konnte nicht geändert werden');
    }
  };

  const submitNewFolder = () => {
    const name = newFolderName.trim();
    if (!name) return;
    setActiveFolder(name);
    setNewFolderName('');
    setShowNewFolderInput(false);
    toast.success(`Ordner „${name}" angelegt — lade eine Datei hoch, um ihn zu speichern.`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  return (
    <Card className="overflow-hidden" data-testid="tresor">
      <div className="flex min-h-[480px] flex-col sm:flex-row" onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}>
        {/* Sidebar */}
        <aside className="w-full border-b border-slate-200 bg-white py-3 sm:w-60 sm:border-b-0 sm:border-r sm:py-4">
          <p className="mb-2 px-4 text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Ordner</p>
          <div className="flex flex-row sm:flex-col overflow-x-auto">
            {folders.map((f) => {
              const isActive = activeFolder === f.name;
              return (
                <button
                  key={f.name}
                  onClick={() => setActiveFolder(f.name)}
                  className={`mx-2 sm:mx-0 mb-1 flex min-w-fit items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors sm:rounded-none sm:border-l-2 sm:px-3 ${
                    isActive
                      ? 'bg-emerald-50 font-medium text-emerald-900 sm:border-emerald-600'
                      : 'text-slate-600 hover:bg-slate-50 sm:border-transparent'
                  }`}
                  data-testid={`folder-${f.name}`}
                >
                  <span className="flex items-center gap-2 whitespace-nowrap">
                    <Folder className={`h-4 w-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                    {f.name}
                  </span>
                  <span className="ml-3 font-mono text-xs text-slate-400">{f.count}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-3 px-4">
            {showNewFolderInput ? (
              <div className="flex items-center gap-1">
                <Input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && submitNewFolder()}
                  placeholder="Ordnername…"
                  autoFocus
                  className="h-8 text-xs"
                  data-testid="new-folder-input"
                />
                <button onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }} className="p-1 text-slate-400 hover:text-slate-700">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowNewFolderInput(true)}
                className="w-full rounded-lg border border-dashed border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50"
                data-testid="new-folder-btn"
              >
                + Ordner anlegen
              </button>
            )}
          </div>
        </aside>

        {/* Main */}
        <main className={`flex-1 p-4 sm:p-6 transition-colors ${dragOver ? 'bg-emerald-50/50' : 'bg-slate-50'}`}>
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-emerald-600" />
                <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={H} data-testid="active-folder-title">
                  {activeFolder || 'Dokumente'}
                </h2>
              </div>
              <p className="mt-0.5 font-mono text-xs text-slate-500">
                {documents.length} {documents.length === 1 ? 'Datei' : 'Dateien'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Dateien suchen…"
                  className="pl-9 w-56"
                  data-testid="tresor-search"
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => handleUpload(Array.from(e.target.files))}
                data-testid="file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !activeFolder}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="upload-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? 'Lädt…' : 'Hochladen'}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-200 border-t-emerald-600" />
            </div>
          ) : documents.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white px-6 py-12 text-center">
              <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm font-medium text-slate-900">
                {dragOver ? 'Loslassen zum Hochladen' : `Keine Dateien in „${activeFolder}"`}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {dragOver ? '' : 'Datei per Drag & Drop hier ablegen oder „Hochladen" klicken.'}
              </p>
            </div>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Datum</th>
                    <th className="px-4 py-3 font-medium">Von</th>
                    <th className="px-4 py-3 font-medium">Größe</th>
                    <th className="px-4 py-3 text-right font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {documents.map((d) => (
                    <tr key={d.id} className="hover:bg-slate-50" data-testid={`doc-${d.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-900">{d.name}</span>
                          {d.version > 1 && (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-medium text-slate-600">v{d.version}</span>
                          )}
                          {d.is_read_only && (
                            <span title="Schreibgeschützt"><Lock className="h-3 w-3 text-slate-400" /></span>
                          )}
                          {d.quarter && (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-600">{d.quarter}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDate(d.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-mono text-[10px] font-semibold ${
                            d.uploader_type === 'advisor' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                          }`}>
                            {(d.uploader_name || '?')[0].toUpperCase()}
                          </span>
                          <span className="text-xs text-slate-700 truncate max-w-[120px]">{d.uploader_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtSize(d.size_bytes)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => handleDownload(d)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Download" data-testid={`download-${d.id}`}>
                          <Download className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleToggleReadOnly(d)} className={`rounded p-1 hover:bg-slate-100 ${d.is_read_only ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-700'}`} title={d.is_read_only ? 'Schreibschutz entfernen' : 'Schreibgeschützt markieren'} data-testid={`lock-${d.id}`}>
                          <ShieldCheck className="h-4 w-4" />
                        </button>
                        {!d.is_read_only && (
                          <button onClick={() => handleDelete(d)} className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Löschen" data-testid={`delete-${d.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <p className="mt-3 text-[11px] text-slate-400">
            Max. {MAX_SIZE_MB} MB pro Datei. Bei gleichem Dateinamen wird automatisch eine neue Version angelegt.
          </p>
        </main>
      </div>
    </Card>
  );
}
